/**
 * The audit log.
 *
 * Visual Reference: `design/screens/settings-audit.html` — default, loading,
 * empty, error. Its own note is the brief: "events grouped by day, each read as
 * a sentence a person could say out loud. Actor, time and IP sit beneath in
 * 13px slate, so the record is complete without the sentence turning into a
 * system log."
 *
 * "This record can't be changed or removed" is the subtitle, and it is true
 * because of migration 0001's triggers rather than because this page has no
 * buttons. There is no PATCH and no DELETE in the contract for `audit`, and the
 * authz gate 404s anything undeclared.
 *
 * THE IP IS RENDERED IN FULL — DECISION-025. `org_settings.ip_capture_mode`
 * governs respondent attribution and has no effect here; PRD §3.16 requires
 * every credential event carry an IP, and a log that anonymises its own admins
 * is not evidence of anything.
 *
 * Story 4.5.
 */
import { useCallback, useEffect, useState } from 'react'
import { CreditCard, Download, FileText, KeyRound, Settings2, Users } from 'lucide-react'
import { apiGet, ApiFailure } from '../../lib/api'
import { useMe } from '../../shell/useMe'
import { Button } from '../../ui/Button'
import { Pill } from '../../ui/Pill'
import { Select } from '../../ui/Select'
import { Skeleton } from '../../ui/Skeleton'

type Category = 'all' | 'members' | 'credentials' | 'funnels' | 'billing' | 'exports'

type AuditRow = {
  id: string
  summary: string
  action: string
  actorName: string | null
  actorIp: string | null
  createdAt: number
  category: Category
}

type AuditPage = {
  rows: AuditRow[]
  hasMore: boolean
  categories: { key: Category; label: string }[]
}

/**
 * The 26px category square the mockup draws to the left of every sentence.
 *
 * `DESIGN-SYSTEM.md` flags icon slots as unfilled and ARCH §7.3 fills them with
 * Lucide, which is what Story 3.8's sidebar did. The COLOUR is the export's:
 * brand for a member change, slate for a credential. The rest follow the same
 * logic — nothing here is a band, so nothing uses a band colour.
 */
const CATEGORY_ICON: Record<Category, { icon: typeof Users; tone: string }> = {
  all: { icon: Settings2, tone: 'bg-mdx-slate' },
  members: { icon: Users, tone: 'bg-mdx-brand' },
  credentials: { icon: KeyRound, tone: 'bg-mdx-slate' },
  funnels: { icon: FileText, tone: 'bg-mdx-brand-deep' },
  billing: { icon: CreditCard, tone: 'bg-mdx-nurture' },
  exports: { icon: Download, tone: 'bg-mdx-slate' },
}

/** The plan the export first appears on — `services/entitlements` says Scale. */
const EXPORT_PLAN = 'Scale'

/**
 * The "Dates" control, which the mockup draws as a chevron button reading
 * "1 – 23 August 2026".
 *
 * PRESETS, not a calendar. The drawn control shows one resolved range with a
 * chevron, which is a select whose options are ranges — and a two-date picker
 * is a component nothing else in the product needs yet. The option TEXT is the
 * resolved range, so the closed control reads exactly as the export draws it.
 */
const RANGES = [
  { key: 'month', label: 'This month', days: null as number | null, monthToDate: true },
  { key: 'week', label: 'Last 7 days', days: 7, monthToDate: false },
  { key: 'thirty', label: 'Last 30 days', days: 30, monthToDate: false },
  { key: 'all', label: 'All time', days: null, monthToDate: false },
] as const

type RangeKey = (typeof RANGES)[number]['key']

/** `from` in epoch ms, or undefined for all time. */
function rangeStart(key: RangeKey, now = Date.now()): number | undefined {
  const spec = RANGES.find((range) => range.key === key)!
  if (spec.monthToDate) {
    const date = new Date(now)
    return new Date(date.getFullYear(), date.getMonth(), 1).getTime()
  }
  if (spec.days === null) return undefined
  const date = new Date(now)
  date.setHours(0, 0, 0, 0)
  return date.getTime() - (spec.days - 1) * 86_400_000
}

/** "1 – 24 August 2026" · "All time". */
function rangeLabel(key: RangeKey, now = Date.now()): string {
  const from = rangeStart(key, now)
  if (from === undefined) return 'All time'
  const start = new Date(from)
  const end = new Date(now)
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const month = end.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  return sameMonth
    ? `${start.getDate()} – ${end.getDate()} ${month}`
    : `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} – ${end.getDate()} ${month}`
}

export function AuditLog() {
  const me = useMe()
  const org = me.status === 'ready' ? me.me.orgs[0] : undefined
  const entitlements = me.status === 'ready' && org ? me.me.entitlements[org.id] : undefined
  const canExport = entitlements?.flags?.['audit_export'] === true

  const [category, setCategory] = useState<Category>('all')
  const [range, setRange] = useState<RangeKey>('month')
  const [page, setPage] = useState<AuditPage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [older, setOlder] = useState<AuditRow[]>([])

  const load = useCallback(async () => {
    if (!org) return
    setError(null)
    setOlder([])
    try {
      const from = rangeStart(range)
      setPage(
        await apiGet<AuditPage>(
          `/v1/orgs/${org.id}/audit?category=${category}${from === undefined ? '' : `&from=${from}`}`,
        ),
      )
    } catch (failure) {
      setPage(null)
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    }
  }, [org, category, range])

  useEffect(() => {
    void load()
  }, [load])

  const showEarlier = async () => {
    if (!org || !page) return
    const rows = [...page.rows, ...older]
    const oldest = rows[rows.length - 1]
    if (!oldest) return
    try {
      const from = rangeStart(range)
      const next = await apiGet<AuditPage>(
        `/v1/orgs/${org.id}/audit?category=${category}&before=${oldest.createdAt}` +
          (from === undefined ? '' : `&from=${from}`),
      )
      setOlder((current) => [...current, ...next.rows])
      setPage({ ...page, hasMore: next.hasMore })
    } catch (failure) {
      setError(failure instanceof ApiFailure ? failure.message : 'Something went wrong.')
    }
  }

  const header = (
    <>
      <div className="flex flex-col gap-mdx-8">
        <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
          Audit log
        </h1>
        <p className="m-0 text-mdx-16 text-mdx-slate">This record can't be changed or removed.</p>
      </div>

      <div className="flex flex-wrap items-end gap-x-mdx-16 gap-y-mdx-12">
        <Select
          label="Dates"
          showLabel
          wide
          className="flex-1 basis-mdx-filter-column"
          value={range}
          onChange={(event) => setRange(event.target.value as RangeKey)}
        >
          {RANGES.map((option) => (
            <option key={option.key} value={option.key}>
              {rangeLabel(option.key)}
            </option>
          ))}
        </Select>

        <Select
          label="Filter events"
          showLabel
          wide
          className="flex-1 basis-mdx-filter-column"
          value={category}
          onChange={(event) => setCategory(event.target.value as Category)}
        >
          {(page?.categories ?? [{ key: 'all' as Category, label: 'All events' }]).map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </Select>

        {/**
         * The disabled button NAMES THE PLAN rather than merely greying out —
         * the story's criterion, and the mockup's own title attribute. The 403
         * names it too (`services/entitlements`' `FIRST_PLAN_WITH`), so the
         * screen and the server say the same word.
         */}
        <Button
          variant="secondary"
          disabled={!canExport}
          title={
            canExport
              ? undefined
              : `Exporting the audit log is available on ${EXPORT_PLAN} and above.`
          }
          onClick={() => {
            if (org) window.location.assign(`/v1/orgs/${org.id}/audit/export`)
          }}
        >
          Export
          {canExport ? null : (
            <Pill tone="brand" className="px-mdx-10 py-mdx-3 text-mdx-12 font-mdx-bold">
              {EXPORT_PLAN}
            </Pill>
          )}
        </Button>
      </div>
    </>
  )

  /* ── loading ──────────────────────────────────────────────────────────── */
  if (!page && !error) {
    return (
      <>
        {header}
        <Card>
          {/* Eight rows at the real geometry, so nothing shifts when they land. */}
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="flex items-start gap-mdx-16 px-mdx-16 py-mdx-14">
              <Skeleton className="size-mdx-evidence-icon rounded-mdx-control-sm" />
              <Skeleton className="h-mdx-32 flex-1" />
            </div>
          ))}
        </Card>
      </>
    )
  }

  /* ── error ────────────────────────────────────────────────────────────── */
  if (!page) {
    return (
      <>
        {header}
        <Card className="items-center py-mdx-48 text-center">
          <span
            aria-hidden="true"
            className="flex size-mdx-40 shrink-0 items-center justify-center rounded-mdx-control bg-mdx-error text-mdx-20 font-mdx-bold text-mdx-white"
          >
            !
          </span>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Couldn't load the audit log
          </span>
          <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
            The record itself is intact — this page couldn't read it. Try again, and if it keeps
            failing, narrow the date range.
          </span>
          <Button onClick={() => void load()}>Try again</Button>
        </Card>
      </>
    )
  }

  const rows = [...page.rows, ...older]

  /* ── empty ────────────────────────────────────────────────────────────── */
  if (rows.length === 0) {
    return (
      <>
        {header}
        <Card className="items-center py-mdx-48 text-center">
          <span aria-hidden="true" className="size-mdx-40 shrink-0 rounded-mdx-pill bg-mdx-line" />
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Nothing has happened yet
          </span>
          <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
            Changes to your team, keys, funnels and plan are recorded here.
          </span>
          {/* An empty state is an invitation to act — and inviting somebody is
              what generates the first event. */}
          <Button onClick={() => window.location.assign('/team')}>Invite your team</Button>
        </Card>
      </>
    )
  }

  return (
    <>
      {header}
      <section className="flex flex-col gap-mdx-22 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-20 shadow-mdx-card">
        {groupByDay(rows).map((group) => (
          <div key={group.label} className="flex flex-col gap-mdx-10">
            <span className="px-mdx-4 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
              {group.label}
            </span>
            <div className="flex flex-col gap-mdx-2">
              {group.rows.map((row) => (
                <Event key={row.id} row={row} />
              ))}
            </div>
          </div>
        ))}

        {page.hasMore ? (
          <button
            type="button"
            onClick={() => void showEarlier()}
            className="cursor-pointer self-start border-0 bg-transparent px-mdx-4 text-mdx-15 font-mdx-semibold text-mdx-brand-deep"
          >
            Show earlier events
          </button>
        ) : null}
      </section>
    </>
  )
}

/* ── the pieces ─────────────────────────────────────────────────────────── */

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={[
        'flex flex-col gap-mdx-22 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-20 shadow-mdx-card',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </section>
  )
}

/**
 * One event: the square, the sentence, and the record beneath it.
 *
 * The sentence is 16px INK and the line under it is 13px slate — the export's
 * own hierarchy, and the reason the note says the record stays complete
 * "without the sentence turning into a system log". The action code never
 * appears; `events.summary` is the whole of what a person reads.
 */
function Event({ row }: { row: AuditRow }) {
  const { icon: Icon, tone } = CATEGORY_ICON[row.category]
  return (
    <div className="flex flex-wrap items-start gap-x-mdx-16 gap-y-mdx-6 rounded-mdx-control px-mdx-16 py-mdx-14 transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground">
      <span
        aria-hidden="true"
        className={`mt-mdx-2 flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm ${tone}`}
      >
        <Icon className="size-mdx-14 text-mdx-white" strokeWidth={2} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-mdx-2">
        <span className="text-mdx-16">{row.summary}</span>
        <span className="text-mdx-13 tabular-nums text-mdx-slate">
          {/* Actor · time · IP IN FULL — DECISION-025. */}
          {[row.actorName ?? 'Leadgible', clockOf(row.createdAt), row.actorIp]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    </div>
  )
}

/* ── formatting ─────────────────────────────────────────────────────────── */

/** "Today · Sunday 23 August" · "Yesterday · Saturday 22 August" · "Thursday 20 August". */
function dayLabel(at: number, now = Date.now()): string {
  const day = new Date(at)
  const full = day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
  const midnight = (value: number) => {
    const date = new Date(value)
    date.setHours(0, 0, 0, 0)
    return date.getTime()
  }
  const days = Math.round((midnight(now) - midnight(at)) / 86_400_000)
  if (days === 0) return `Today · ${full}`
  if (days === 1) return `Yesterday · ${full}`
  return full
}

/** 24-hour, because the log is a record rather than a conversation. */
const clockOf = (at: number): string =>
  new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

function groupByDay(rows: AuditRow[]): { label: string; rows: AuditRow[] }[] {
  const groups: { label: string; rows: AuditRow[] }[] = []
  for (const row of rows) {
    const label = dayLabel(row.createdAt)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.rows.push(row)
    else groups.push({ label, rows: [row] })
  }
  return groups
}
